import Logo from "./Logo";
import Shield from "lucide-react/dist/esm/icons/shield";
import ExternalLink from "lucide-react/dist/esm/icons/external-link";
import { Link } from "react-router-dom";
import { CookieSettingsDialog } from "./compliance/CookieSettingsDialog";
import { useAuth } from "@/contexts/AuthContext";
import { footerConfig, trustBlockContent } from "@/lib/navigationConfig";
import { isExternalLink, MAIN_SITE_LINKS } from "@/lib/domainConfig";
import { useModuleCTA, moduleFromHref } from "@/hooks/useModuleCTA";

type FooterItem = { label: string; href: string; external?: boolean };

const FooterLink = ({ item }: { item: FooterItem }) => {
  const external = item.external || isExternalLink(item.href);
  const isMailto = item.href.startsWith("mailto:");
  const cls = "text-sm text-muted-foreground hover:text-primary transition-colors focus-visible:ring-2 focus-visible:ring-primary rounded";
  const { trackModuleCTA } = useModuleCTA();
  const mod = moduleFromHref(item.href);
  const onClick = () => {
    if (mod) {
      trackModuleCTA({ module: mod, surface: "footer", action: "learn_more", destination: item.href, label: item.label });
    }
  };
  if (external || isMailto) {
    return (
      <a
        href={item.href}
        target={external && !isMailto ? "_blank" : undefined}
        rel={external && !isMailto ? "noopener" : undefined}
        className={`${cls} inline-flex items-center gap-1`}
        onClick={onClick}
      >
        {item.label}
        {external && !isMailto && <ExternalLink className="w-3 h-3" />}
      </a>
    );
  }
  return <Link to={item.href} className={cls} onClick={onClick}>{item.label}</Link>;
};

const Footer = () => {
  const { user } = useAuth();

  // Authenticated hub footer: minimal — Help, Privacy, Terms, Accessibility only.
  if (user) {
    return (
      <footer className="border-t border-border bg-card/50">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Shield className="w-4 h-4 text-primary" />
              <span>FERPA & COPPA Compliant</span>
            </div>
            <ul className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
              <li><Link to="/support" className="hover:text-primary transition-colors">Help</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy</Link></li>
              <li><Link to="/terms-of-service" className="hover:text-primary transition-colors">Terms</Link></li>
              <li><Link to="/accessibility" className="hover:text-primary transition-colors">Accessibility</Link></li>
              <li><Link to="/legal/data-retention" className="hover:text-primary transition-colors">Data</Link></li>
              <li><Link to="/legal-center" className="hover:text-primary transition-colors">Legal Center</Link></li>
              <li>
                <CookieSettingsDialog
                  trigger={
                    <button className="hover:text-primary transition-colors">Cookies</button>
                  }
                />
              </li>
            </ul>
            <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} CastReady</p>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t border-border bg-card/50">
      <div className="container mx-auto px-6 py-12">
        {/* Trust Block */}
        <div className="mb-10 p-6 bg-primary/5 rounded-xl border border-primary/10">
          <div className="flex items-start gap-4">
            <Shield className="w-8 h-8 text-primary shrink-0 mt-1" />
            <div>
              <h3 className="font-display font-semibold text-foreground mb-2">Safety & Trust Are Our Priority</h3>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">{trustBlockContent}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Logo className="mb-4" />
            <p className="text-muted-foreground text-sm leading-relaxed">
              One ecosystem for everyone behind the performance.
            </p>
            <div className="mt-4 pt-4 border-t border-border/30">
              <a
                href={MAIN_SITE_LINKS.home}
                className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1.5"
                rel="noopener"
              >
                Visit CastReady.org
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-display font-semibold text-foreground mb-4">Product</h4>
            <ul className="space-y-2">
              {footerConfig.product.map((item) => (
                <li key={item.href}><FooterLink item={item} /></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-foreground mb-4">Company</h4>
            <ul className="space-y-2">
              {footerConfig.company.map((item) => (
                <li key={item.href}><FooterLink item={item} /></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-foreground mb-4">Trust &amp; Legal</h4>
            <ul className="space-y-2">
              {footerConfig.trust.map((item) => (
                <li key={item.href}><FooterLink item={item} /></li>
              ))}
              <li>
                <CookieSettingsDialog
                  trigger={
                    <button className="text-sm text-muted-foreground hover:text-primary transition-colors focus-visible:ring-2 focus-visible:ring-primary rounded">
                      Cookie Settings
                    </button>
                  }
                />
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-border">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" />
                <span>FERPA & COPPA Compliant</span>
              </div>
              <Link to="/status" className="hover:text-primary transition-colors">Status</Link>
              <a href="https://castready.org/newsroom" target="_blank" rel="noopener" className="hover:text-primary transition-colors">Changelog</a>
            </div>

            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} CastReady. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
